import React, { Fragment } from "react";
import get from "lodash/get";
import { useFormikContext } from "formik";
import Toggle from "react-toggle";
import {
  generateSelectOptions,
  getFieldPath,
  getToggleProps
} from "utils/form";
import { RESERVED_PATTERNS } from "utils/constants/fields";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import MultiSelect from "components/Form/fields/MultiSelect";
import { EMPTY_SELECT_OPTION } from "utils/constants/ui";
import { useAppCheckout } from "utils/selectors";
import { STATE_KEYS } from "utils/constants/state";
import { COMMAND_CONTENT_ID } from "components/CommandPalette/constants";
import PlayVideoTrigger from "components/PlayVideoTrigger";
import ServiceActionOption from "./ServiceActionOption";

const DEFAULT_LABEL = "Connect";
const DEFAULT_DESCRIPTION = "Connect your field data to other services.";

const CONNECT_STRIPE_CUSTOMER_FROM_QUERY_PARAMS = {
  enabled: true,
  actions: {
    "9c3a4430-c554-4535-833a-bd1e5a7ef6a7": true
  }
};

const CONNECT_DEFAULT = {
  enabled: true,
  actions: null
};

/**
 * TODO: add field level configuration like `priority` when connect option has configuration options
 */
const Connect = ({
  name,
  label = DEFAULT_LABEL,
  description = DEFAULT_DESCRIPTION
}) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const checkout = useAppCheckout();
  const actionOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.CONNECT_ACTION_OPTIONS) || [];

  const validationPatternPath = getFieldPath(name, "validation.pattern");
  const validationPattern = get(values, validationPatternPath);

  const validationRequiredPath = getFieldPath(name, "validation.required");
  const validationRequired = get(values, validationRequiredPath);

  const emailPattern = validationPattern === RESERVED_PATTERNS.EMAIL;
  const requiredEmailValidation = validationRequired && emailPattern;

  const connectEnableTouched = Boolean(get(connectEnabledPath, touched));

  const connectPath = getFieldPath(name, "connect");
  const connectConfig = get(values, connectPath);

  const hasConnectConfig =
    !isUndefined(connectConfig) && !isNull(connectConfig);

  const connectEnabledPath = getFieldPath(name, "connect.enabled");

  const connectEnabled = Boolean(get(values, connectEnabledPath));

  const connectDestinationsPath = getFieldPath(name, "connect.actions");
  const connectDestinations = get(values, connectDestinationsPath);
  const connectDestinationsTouched = get(touched, connectDestinationsPath);
  const selectedDestinations = actionOptions.filter(
    (option) => connectDestinations && connectDestinations[option.uuid]
  );
  let actionsSelections = [];
  if (selectedDestinations.length > 0) {
    actionsSelections = generateSelectOptions(selectedDestinations);
  }

  const actionsSelectProps = {
    value: connectEnabled ? actionsSelections : [],
    placeholder: connectEnabled ? "Select action" : "Not enabled",
    disabled: !connectEnabled,
    options: [EMPTY_SELECT_OPTION, ...generateSelectOptions(actionOptions)],
    onChange: () => (selectedDestination) => {
      const updatedValue =
        selectedDestination && selectedDestination.uuid
          ? { [selectedDestination.uuid]: true }
          : null;
      setFieldValue(connectDestinationsPath, updatedValue);

      if (!connectDestinationsTouched) {
        setFieldTouched(connectDestinationsPath, true);
      }
    },
    isMulti: false,
    labelTransform: (value) => value,
    components: {
      Option: ServiceActionOption
    }
  };

  return (
    <Fragment>
      <div className="w-100 flex flex-column">
        <div className="w-100 flex flex-row justify-between items-center">
          <label className="f6 dib fw6">{label}</label>
          <div className="flex flex-row">
            <PlayVideoTrigger id={COMMAND_CONTENT_ID.FIELDS.CONNECT} />
            <div className="dib bl hairline-1 pl2 ml2">
              <Toggle
                {...getToggleProps({
                  active: connectEnabled,
                  setFieldValue: (_, active) => {
                    setFieldValue(connectEnabledPath, active);
                    const needsDefaultConfig = active && !hasConnectConfig;
                    if (needsDefaultConfig) {
                      setFieldValue(
                        connectPath,
                        requiredEmailValidation
                          ? CONNECT_STRIPE_CUSTOMER_FROM_QUERY_PARAMS
                          : CONNECT_DEFAULT
                      );
                    }

                    if (!connectEnableTouched) {
                      setFieldTouched(connectEnabledPath, true);
                    }
                  }
                })}
              />
            </div>
          </div>
        </div>
        <div className="f6 pt2 lh-title">{description}</div>
        {connectEnabled && (
          <Fragment>
            <div className="pt3">
              <label className="f6 dib fw5">Service action</label>
              <div className="pt2 z-2 relative">
                <MultiSelect {...actionsSelectProps} />
              </div>
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default Connect;
